import React, { useState, useMemo, useCallback } from 'react';
import { useEditorId } from './hooks/useEditorId';
import { IEditorProps } from './Editor';
import { IPropertyChild, getChildValueAsArray } from 'components/property-editors/utils/ProviderNodeUtils';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EditorLabel } from './EditorLabel';
import { EditorValidationWrapper } from './EditorValidationWrapper';
import './GroupedListEditor.less';
import classNames from 'classnames';
import { DataPair } from '@decisions/server/DecisionsFramework/ServiceLayer/Services/ContextData/DataPair';

interface IGroupedListEditorProps extends IEditorProps {}

export function GroupedListEditor({ child, path, onChange }: IGroupedListEditorProps) {
  const blockClassName = 'dpc-grouped-string-editor';
  const [id] = useEditorId(path);
  const [selected, setSelected] = useState<string[]>(getChildValueAsArray(child));
  const options = useMemo(() => getOptions(child), [child]);

  const getGroupContainerClass = (name: string) =>
    classNames(`${blockClassName}__group`, {
      [`${blockClassName}__group--active`]: selected.indexOf(name) >= 0,
    });

  const getAllGroupContainerClass = () =>
    classNames(`${blockClassName}__group`, `${blockClassName}__group-all`, {
      [`${blockClassName}__group--active`]: selected.length === 0,
    });

  const changeHandler = useCallback(
    (value: string) => {
      let newValue = [...selected];
      if (selected.includes(value)) {
        newValue.splice(selected.indexOf(value), 1);
      } else {
        newValue.push(value);
      }

      setSelected(newValue);
      onChange(newValue, path);
    },
    [onChange, path, selected]
  );

  const getAllItemsView = () => {
    return (
      <div
        className={getAllGroupContainerClass()}
        onClick={() => {
          setSelected([]);
          onChange([], path);
        }}
      >
        <span className={`${blockClassName}__name`}>All</span>
      </div>
    );
  };

  if (!options || !options.length) return null;
  return (
    <EditorLabel htmlFor={id} editor={child} path={path} doNotWrap>
      <EditorValidationWrapper editor={child}>
        <div className={`${blockClassName}`}>
          {getAllItemsView()}
          {options.map(value => (
            <div
              className={getGroupContainerClass(value.Name)}
              key={value.Name}
              onClick={() => {
                changeHandler(value.Name);
              }}
            >
              <span className={`${blockClassName}__name`}>{value.Name}</span>
              <span className={`${blockClassName}__counter`}>{value.OutputValue}</span>
            </div>
          ))}
        </div>
      </EditorValidationWrapper>
    </EditorLabel>
  );
}

function getOptions(child: IPropertyChild): DataPair[] {
  return (getEditorConfigValue(child, 'values') || []).filter((value?: string | null) => {
    if (!value) {
      console.log('Improper Configuration: falsy value passed as Checklist entry name');
      return false;
    }
    return true;
  });
}
